import { useState } from 'react';
import { Tag, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline tag editor for a watchlist item.
 * Renders chips + a small input to add new tags. Persists to backend via PATCH.
 */
export function WatchlistTags({ item, onSaved }) {
  const [tags, setTags] = useState(item.tags || []);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const persist = async (nextTags) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/watchlist/${item.id}/tags`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tags: nextTags }),
      });
      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();
      setTags(data.tags);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error('Could not save tags');
      setTags(item.tags || []);
    } finally {
      setSaving(false);
    }
  };

  const addTag = async () => {
    const clean = draft.trim();
    if (!clean) { setAdding(false); return; }
    if (tags.includes(clean)) { setDraft(''); setAdding(false); return; }
    if (tags.length >= 10) { toast.error('Max 10 tags per grant'); return; }
    const next = [...tags, clean];
    setDraft('');
    setAdding(false);
    persist(next);
  };

  const removeTag = (tag) => {
    persist(tags.filter(t => t !== tag));
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid={`watchlist-tags-${item.id}`}>
      {tags.length === 0 && !adding && (
        <span className="text-xs text-stone-400 flex items-center gap-1">
          <Tag className="w-3 h-3" />
          No tags
        </span>
      )}

      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 text-xs bg-primary/8 text-primary px-2 py-0.5 rounded-full font-medium"
          data-testid={`tag-chip-${tag}`}
        >
          {tag}
          <button
            type="button"
            onClick={() => removeTag(tag)}
            className="text-primary/60 hover:text-primary"
            aria-label={`Remove ${tag}`}
            data-testid={`tag-remove-${tag}`}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}

      {adding ? (
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 40))}
          onBlur={addTag}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); addTag(); }
            if (e.key === 'Escape') { setDraft(''); setAdding(false); }
          }}
          placeholder="Tag..."
          autoFocus
          disabled={saving}
          className="text-xs border border-stone-200 rounded-full px-2.5 py-0.5 w-24 focus:outline-none focus:border-primary"
          data-testid={`tag-input-${item.id}`}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-primary border border-dashed border-stone-300 hover:border-primary rounded-full px-2 py-0.5"
          data-testid={`tag-add-${item.id}`}
        >
          <Plus className="w-2.5 h-2.5" /> Add tag
        </button>
      )}
    </div>
  );
}
